// src/components/Branch/config/sections/CertificationsSection.jsx
import React, { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  BadgeCheckIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  UploadIcon,
  ExternalLinkIcon,
} from "@heroicons/react/outline";
import {
  addCertificationThunk,
  updateCertificationThunk,
  deleteCertificationThunk,
} from "../../../../store/thunks/branchDisplayThunks";
import Dropdown from "../../../common/Dropdown";

const CERTIFICATION_TYPES = [
  { _id: "BIS_HALLMARK", name: "BIS Hallmark" },
  { _id: "ISO", name: "ISO Certification" },
  { _id: "GIA", name: "GIA Certified" },
  { _id: "IGI", name: "IGI Certified" },
  { _id: "GUILD_MEMBERSHIP", name: "Guild Membership" },
  { _id: "TRADE_LICENSE", name: "Trade License" },
  { _id: "OTHER", name: "Other" },
];

const INITIAL_FORM = {
  type: "",
  name: "",
  issuedBy: "",
  certificateNumber: "",
  validUntil: "",
  verificationUrl: "",
  isVisible: true,
};

const CertificationsSection = ({ branchCode, isRootAdmin }) => {
  const dispatch = useDispatch();
  const { certifications, uploading, loadingSection } = useSelector(
    (state) => state.branchDisplay
  );
  const fileInputRef = useRef(null);

  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAdd = async () => {
    const formDataObj = new FormData();
    if (selectedFile) formDataObj.append("image", selectedFile);
    Object.entries(formData).forEach(([key, value]) => {
      formDataObj.append(key, value);
    });

    await dispatch(
      addCertificationThunk({ branchCode, formData: formDataObj })
    );
    handleCancel();
  };

  const handleEdit = (cert) => {
    setEditingId(cert._id);
    setFormData({
      type: cert.type || "",
      name: cert.name || "",
      issuedBy: cert.issuedBy || "",
      certificateNumber: cert.certificateNumber || "",
      validUntil: cert.validUntil ? cert.validUntil.split("T")[0] : "",
      verificationUrl: cert.verificationUrl || "",
      isVisible: cert.isVisible ?? true,
    });
  };

  const handleUpdate = async () => {
    await dispatch(
      updateCertificationThunk({
        branchCode,
        certificationId: editingId,
        data: formData,
      })
    );
    handleCancel();
  };

  const handleDelete = async (certId) => {
    if (window.confirm("Delete this certification?")) {
      await dispatch(
        deleteCertificationThunk({ branchCode, certificationId: certId })
      );
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData(INITIAL_FORM);
    setSelectedFile(null);
  };

  const renderForm = () => (
    <div className="bg-white border rounded-lg p-4 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Dropdown
          label="Certification Type"
          options={CERTIFICATION_TYPES}
          selected={CERTIFICATION_TYPES.find((t) => t._id === formData.type)}
          onChange={(selected) =>
            handleInputChange("type", selected?._id || "")
          }
          required
        />
        <div>
          <label className="block text-sm text-gray-600 mb-1">Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="Certification name"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-600 mb-1">Issued By</label>
          <input
            type="text"
            value={formData.issuedBy}
            onChange={(e) => handleInputChange("issuedBy", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="Issuing authority"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">
            Certificate Number
          </label>
          <input
            type="text"
            value={formData.certificateNumber}
            onChange={(e) =>
              handleInputChange("certificateNumber", e.target.value)
            }
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="Certificate number"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-600 mb-1">
            Valid Until
          </label>
          <input
            type="date"
            value={formData.validUntil}
            onChange={(e) => handleInputChange("validUntil", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">
            Verification URL
          </label>
          <input
            type="url"
            value={formData.verificationUrl}
            onChange={(e) =>
              handleInputChange("verificationUrl", e.target.value)
            }
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="https://..."
          />
        </div>
      </div>

      {isAdding && (
        <div>
          <label className="block text-sm text-gray-600 mb-1">
            Certificate Image
          </label>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/*"
            onChange={(e) => setSelectedFile(e.target.files[0])}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="px-3 py-2 border rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <UploadIcon className="h-4 w-4" />
            {selectedFile ? selectedFile.name : "Select Image"}
          </button>
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <button
          onClick={editingId ? handleUpdate : handleAdd}
          disabled={
            loadingSection === "certifications" || uploading || !formData.name
          }
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
        >
          <CheckCircleIcon className="h-4 w-4" />
          {editingId ? "Update" : "Add"} Certification
        </button>
        <button
          onClick={handleCancel}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {isRootAdmin && !isAdding && !editingId && (
        <button
          onClick={() => setIsAdding(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <PlusIcon className="h-4 w-4" />
          Add Certification
        </button>
      )}

      {(isAdding || editingId) && renderForm()}

      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium mb-4 flex items-center">
          <BadgeCheckIcon className="h-4 w-4 mr-2" />
          Certifications ({certifications.length})
        </h4>

        {certifications.length === 0 ? (
          <p className="text-center py-8 text-gray-500">
            No certifications added
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {certifications.map((cert) => (
              <div key={cert._id} className="bg-white border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex gap-3">
                    {cert.imageUrl && (
                      <img
                        src={cert.imageUrl}
                        alt={cert.name}
                        className="w-16 h-16 object-contain"
                      />
                    )}
                    <div>
                      <h5 className="font-medium">{cert.name}</h5>
                      <p className="text-sm text-gray-500">
                        {
                          CERTIFICATION_TYPES.find((t) => t._id === cert.type)
                            ?.name
                        }
                      </p>
                      {cert.issuedBy && (
                        <p className="text-xs text-gray-500">
                          By: {cert.issuedBy}
                        </p>
                      )}
                      {cert.validUntil && (
                        <p className="text-xs text-gray-500">
                          Valid until:{" "}
                          {new Date(cert.validUntil).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  {isRootAdmin && !editingId && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleEdit(cert)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(cert._id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CertificationsSection;
